<template>
  <!-- FAB -->
  <button
    @click="abrirDrawer"
    :class="[
      'fixed bottom-6 right-6 z-40',
      'w-13 h-13 rounded-full shadow-xl',
      'bg-gradient-to-br from-violet-500 to-violet-700',
      'hover:from-violet-400 hover:to-violet-600',
      'flex items-center justify-center',
      'transition-all duration-200 hover:scale-110 active:scale-95',
      'group',
      drawerOpen ? 'opacity-0 pointer-events-none scale-75' : 'opacity-100'
    ]"
    title="Cotizador de papel"
  >
    <!-- Icono tijeras -->
    <svg class="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
      <circle cx="6" cy="6" r="2.5"/>
      <circle cx="6" cy="18" r="2.5"/>
      <line x1="8.3" y1="7.4" x2="20" y2="19"/>
      <line x1="8.3" y1="16.6" x2="14" y2="11"/>
      <line x1="16" y1="9" x2="20" y2="5"/>
    </svg>
    <!-- Tooltip -->
    <span class="absolute right-14 bg-gray-900 text-white text-xs px-2.5 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg">
      Cotizador de papel
    </span>
  </button>

  <!-- Overlay -->
  <Transition name="fade">
    <div
      v-if="drawerOpen"
      class="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-40"
      @click="cerrarDrawer"
    />
  </Transition>

  <!-- Drawer -->
  <Transition name="drawer">
    <div
      v-if="drawerOpen"
      class="fixed top-0 right-0 h-full w-full sm:w-[480px] z-50 flex flex-col
             bg-white dark:bg-gray-900
             shadow-2xl border-l border-gray-200 dark:border-gray-700"
    >
      <!-- Header del drawer -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-violet-600 to-violet-700 shrink-0">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <svg class="w-4.5 h-4.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <circle cx="6" cy="6" r="2.5"/>
              <circle cx="6" cy="18" r="2.5"/>
              <line x1="8.3" y1="7.4" x2="20" y2="19"/>
              <line x1="8.3" y1="16.6" x2="14" y2="11"/>
              <line x1="16" y1="9" x2="20" y2="5"/>
            </svg>
          </div>
          <div>
            <h2 class="text-white font-bold text-sm leading-tight">Cotizador de Papel</h2>
            <p class="text-violet-200 text-[11px] leading-tight">Calcula cortes óptimos</p>
          </div>
        </div>
        <button
          @click="cerrarDrawer"
          class="w-7 h-7 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors"
        >
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Contenido scrollable -->
      <div class="flex-1 overflow-y-auto p-5 space-y-5">

        <!-- Formulario -->
        <div class="grid grid-cols-2 gap-4">
          <!-- Papel -->
          <div class="col-span-2">
            <p class="text-[10px] font-bold uppercase tracking-widest text-violet-500 dark:text-violet-400 mb-2">Hoja de papel extendida</p>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Lado menor</label>
                <div class="relative">
                  <input
                    v-model.number="form.papelMenor"
                    type="number"
                    min="1"
                    placeholder="ej. 60"
                    class="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition"
                    @input="limpiarResultado"
                  />
                  <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">cm</span>
                </div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Lado mayor</label>
                <div class="relative">
                  <input
                    v-model.number="form.papelMayor"
                    type="number"
                    min="1"
                    placeholder="ej. 90"
                    class="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition"
                    @input="limpiarResultado"
                  />
                  <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">cm</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Corte -->
          <div class="col-span-2">
            <p class="text-[10px] font-bold uppercase tracking-widest text-emerald-500 dark:text-emerald-400 mb-2">Corte deseado</p>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Corte menor</label>
                <div class="relative">
                  <input
                    v-model.number="form.corteMenor"
                    type="number"
                    min="1"
                    placeholder="ej. 7"
                    class="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                    @input="limpiarResultado"
                  />
                  <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">cm</span>
                </div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Corte mayor</label>
                <div class="relative">
                  <input
                    v-model.number="form.corteMayor"
                    type="number"
                    min="1"
                    placeholder="ej. 10"
                    class="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                    @input="limpiarResultado"
                  />
                  <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">cm</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error -->
        <Transition name="fade">
          <div v-if="error" class="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700">
            <svg class="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
            <span class="text-xs text-red-600 dark:text-red-400">{{ error }}</span>
          </div>
        </Transition>

        <!-- Botón calcular -->
        <button
          @click="calcular"
          :disabled="calculando"
          class="w-full py-2.5 rounded-xl font-semibold text-sm text-white
                 bg-gradient-to-r from-violet-500 to-violet-700
                 hover:from-violet-400 hover:to-violet-600
                 disabled:opacity-60 disabled:cursor-not-allowed
                 shadow-md hover:shadow-violet-200 dark:hover:shadow-violet-900
                 transition-all duration-200 active:scale-[0.98]
                 flex items-center justify-center gap-2"
        >
          <svg v-if="!calculando" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-3M9 7V4a1 1 0 011-1h9a1 1 0 011 1v9a1 1 0 01-1 1h-3M9 7h6v6H9V7z"/>
          </svg>
          <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          {{ calculando ? 'Calculando...' : 'Calcular cortes' }}
        </button>

        <!-- Resultado -->
        <Transition name="result">
          <div v-if="resultado" class="space-y-4">

            <!-- Stats cards -->
            <div class="grid grid-cols-3 gap-2.5">
              <div class="rounded-xl p-3 text-center bg-violet-50 dark:bg-violet-900/30 border border-violet-100 dark:border-violet-800">
                <div class="text-2xl font-black text-violet-600 dark:text-violet-400">{{ resultado.mejorTotal }}</div>
                <div class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 font-medium leading-tight">cortes totales</div>
              </div>
              <div class="rounded-xl p-3 text-center bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800">
                <div class="text-2xl font-black text-emerald-600 dark:text-emerald-400">{{ resultado.mejor.cols }}×{{ resultado.mejor.rows }}</div>
                <div class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 font-medium leading-tight">col × fila</div>
              </div>
              <div class="rounded-xl p-3 text-center bg-amber-50 dark:bg-amber-900/30 border border-amber-100 dark:border-amber-800">
                <div class="text-2xl font-black text-amber-600 dark:text-amber-400">{{ resultado.desperdicio }}%</div>
                <div class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 font-medium leading-tight">desperdicio</div>
              </div>
            </div>

            <!-- Comparativa orientaciones -->
            <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div class="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <p class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Comparativa de orientaciones</p>
              </div>
              <div class="divide-y divide-gray-100 dark:divide-gray-700">
                <div
                  v-for="(op, i) in resultado.opciones"
                  :key="i"
                  :class="[
                    'flex items-center justify-between px-3 py-2.5',
                    op.total === resultado.mejorTotal ? 'bg-violet-50 dark:bg-violet-900/20' : ''
                  ]"
                >
                  <div class="flex items-center gap-2">
                    <div :class="[
                      'w-1.5 h-1.5 rounded-full',
                      op.total === resultado.mejorTotal ? 'bg-violet-500' : 'bg-gray-300 dark:bg-gray-600'
                    ]"/>
                    <span class="text-xs text-gray-600 dark:text-gray-300">{{ op.label }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-gray-400 dark:text-gray-500">{{ op.cols }} col × {{ op.rows }} fil</span>
                    <span :class="[
                      'text-sm font-bold',
                      op.total === resultado.mejorTotal ? 'text-violet-600 dark:text-violet-400' : 'text-gray-500 dark:text-gray-400'
                    ]">{{ op.total }}</span>
                    <span v-if="op.total === resultado.mejorTotal" class="text-[10px] font-semibold bg-violet-100 dark:bg-violet-800 text-violet-600 dark:text-violet-300 px-1.5 py-0.5 rounded-full">mejor</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Canvas visualización -->
            <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div class="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <p class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Visualización</p>
                <div class="flex items-center gap-3 text-[10px] text-gray-400">
                  <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm bg-violet-400 opacity-70"></span>corte</span>
                  <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm bg-amber-300 opacity-70"></span>desperdicio</span>
                </div>
              </div>
              <div class="p-4 flex justify-center bg-white dark:bg-gray-900">
                <canvas ref="canvasRef" class="rounded-lg" style="max-width:100%;image-rendering:pixelated" />
              </div>
            </div>

            <!-- Resumen en texto -->
            <div class="rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3">
              <p class="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                De una hoja de <span class="font-semibold text-gray-800 dark:text-white">{{ resultado.pX }}×{{ resultado.pY }} cm</span> se obtienen
                <span class="font-bold text-violet-600 dark:text-violet-400">{{ resultado.mejorTotal }} cortes</span> de
                <span class="font-semibold text-gray-800 dark:text-white">{{ resultado.mejor.cX }}×{{ resultado.mejor.cY }} cm</span>,
                con <span class="font-semibold text-amber-600">{{ resultado.desperdicio }}%</span> de papel desperdiciado
                ({{ resultado.areaDesperdiciadaCm2 }} cm²).
              </p>
            </div>

          </div>
        </Transition>

      </div>

      <!-- Footer del drawer -->
      <div class="px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0">
        <button
          @click="limpiarTodo"
          class="w-full py-2 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Limpiar todo
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, nextTick } from 'vue'

const drawerOpen = ref(false)
const calculando = ref(false)
const error = ref('')
const resultado = ref(null)
const canvasRef = ref(null)

const form = ref({
  papelMenor: null,
  papelMayor: null,
  corteMenor: null,
  corteMayor: null,
})

function abrirDrawer() {
  drawerOpen.value = true
}

function cerrarDrawer() {
  drawerOpen.value = false
}

function limpiarResultado() {
  resultado.value = null
  error.value = ''
}

function limpiarTodo() {
  form.value = { papelMenor: null, papelMayor: null, corteMenor: null, corteMayor: null }
  resultado.value = null
  error.value = ''
}

async function calcular() {
  error.value = ''
  resultado.value = null

  const { papelMenor, papelMayor, corteMenor, corteMayor } = form.value

  if (!papelMenor || !papelMayor || !corteMenor || !corteMayor) {
    error.value = 'Por favor completa todos los campos.'
    return
  }
  if ([papelMenor, papelMayor, corteMenor, corteMayor].some(v => v <= 0)) {
    error.value = 'Todos los valores deben ser mayores a 0.'
    return
  }
  if (corteMenor > papelMenor && corteMayor > papelMayor &&
      corteMayor > papelMenor && corteMenor > papelMayor) {
    error.value = 'El corte es más grande que el papel en todas las orientaciones.'
    return
  }

  calculando.value = true
  await nextTick()
  await new Promise(r => setTimeout(r, 120))

  // El lado mayor es el ancho (horizontal), el menor es el alto — como en el original.
  const pX = papelMayor   // ancho de la hoja (eje horizontal)
  const pY = papelMenor   // alto de la hoja  (eje vertical)

  // Las 2 orientaciones posibles del corte
  const combos = [
    { cX: corteMayor, cY: corteMenor },
    { cX: corteMenor, cY: corteMayor },
  ].map(({ cX, cY }) => {
    const cols = Math.floor(pX / cX)
    const rows = Math.floor(pY / cY)
    return { cols, rows, total: cols * rows, cX, cY }
  })

  const mejor = combos.reduce((a, b) => b.total > a.total ? b : a)
  const areaPapel = pX * pY
  const areaUsada = mejor.cols * mejor.cX * mejor.rows * mejor.cY
  const areaDesperdiciadaCm2 = Math.round(areaPapel - areaUsada)
  const desperdicio = Math.round((areaDesperdiciadaCm2 / areaPapel) * 100)

  // Para la tabla comparativa: etiquetar cada combo
  const opciones = combos.map((c, i) => ({
    ...c,
    label: i === 0 ? `Corte ${c.cX}×${c.cY}` : `Rotado ${c.cX}×${c.cY}`,
  }))

  resultado.value = { opciones, mejor, mejorTotal: mejor.total, desperdicio, areaDesperdiciadaCm2, pX, pY }
  calculando.value = false

  await nextTick()
  dibujarCanvas(pX, pY, mejor)
}

function dibujarCanvas(pX, pY, mejor) {
  const canvas = canvasRef.value
  if (!canvas) return

  const isDark = document.documentElement.classList.contains('dark')

  // Espacio reservado para cotas
  const COTA = 28
  const MAX = 360
  const escala = Math.min(MAX / pX, MAX / pY)
  const W = Math.round(pX * escala)   // ancho del papel en canvas
  const H = Math.round(pY * escala)   // alto del papel en canvas
  const cW = Math.round(mejor.cX * escala)
  const cH = Math.round(mejor.cY * escala)

  // Canvas total = papel + espacio de cotas
  canvas.width  = W + COTA
  canvas.height = H + COTA

  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // ── Dibujar el papel (offset 0,0 — las cotas van fuera) ──

  // Fondo desperdicio
  ctx.fillStyle = isDark ? '#78350f' : '#fde68a'
  ctx.fillRect(0, 0, W, H)

  // Cortes
  for (let r = 0; r < mejor.rows; r++) {
    for (let c = 0; c < mejor.cols; c++) {
      const x = c * cW
      const y = r * cH

      ctx.fillStyle = isDark ? '#6d28d9' : '#a78bfa'
      ctx.fillRect(x + 1, y + 1, cW - 2, cH - 2)

      if (cW > 18 && cH > 14) {
        ctx.fillStyle = isDark ? '#ede9fe' : '#4c1d95'
        ctx.font = `bold ${Math.min(cW, cH) * 0.28}px sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(r * mejor.cols + c + 1, x + cW / 2, y + cH / 2)
      }

      ctx.strokeStyle = '#7c3aed'
      ctx.lineWidth = 0.5
      ctx.strokeRect(x + 1, y + 1, cW - 2, cH - 2)
    }
  }

  // Líneas guillotina
  ctx.strokeStyle = isDark ? '#c4b5fd' : '#5b21b6'
  ctx.lineWidth = 0.8
  ctx.setLineDash([3, 2])
  for (let c = 1; c <= mejor.cols; c++) {
    ctx.beginPath(); ctx.moveTo(c * cW, 0); ctx.lineTo(c * cW, H); ctx.stroke()
  }
  for (let r = 1; r <= mejor.rows; r++) {
    ctx.beginPath(); ctx.moveTo(0, r * cH); ctx.lineTo(W, r * cH); ctx.stroke()
  }
  ctx.setLineDash([])

  // Borde exterior del papel
  ctx.strokeStyle = isDark ? '#8b5cf6' : '#7c3aed'
  ctx.lineWidth = 1.5
  ctx.strokeRect(0, 0, W, H)

  // ── Cotas ──
  const cotaColor = isDark ? '#f87171' : '#dc2626'
  const arrowSize = 5
  ctx.strokeStyle = cotaColor
  ctx.fillStyle = cotaColor
  ctx.lineWidth = 1.2
  ctx.setLineDash([])

  function drawArrowH(x1, x2, y) {
    // línea horizontal con flechas en ambos extremos
    ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke()
    // flecha izq
    ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x1 + arrowSize, y - arrowSize * 0.5); ctx.lineTo(x1 + arrowSize, y + arrowSize * 0.5); ctx.closePath(); ctx.fill()
    // flecha der
    ctx.beginPath(); ctx.moveTo(x2, y); ctx.lineTo(x2 - arrowSize, y - arrowSize * 0.5); ctx.lineTo(x2 - arrowSize, y + arrowSize * 0.5); ctx.closePath(); ctx.fill()
  }

  function drawArrowV(x, y1, y2) {
    // línea vertical con flechas en ambos extremos
    ctx.beginPath(); ctx.moveTo(x, y1); ctx.lineTo(x, y2); ctx.stroke()
    // flecha arriba
    ctx.beginPath(); ctx.moveTo(x, y1); ctx.lineTo(x - arrowSize * 0.5, y1 + arrowSize); ctx.lineTo(x + arrowSize * 0.5, y1 + arrowSize); ctx.closePath(); ctx.fill()
    // flecha abajo
    ctx.beginPath(); ctx.moveTo(x, y2); ctx.lineTo(x - arrowSize * 0.5, y2 - arrowSize); ctx.lineTo(x + arrowSize * 0.5, y2 - arrowSize); ctx.closePath(); ctx.fill()
  }

  const fontSize = Math.max(10, Math.min(13, COTA * 0.45))
  ctx.font = `bold ${fontSize}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // Cota horizontal (ancho = pX) — debajo del papel
  const cotaY = H + COTA * 0.6
  drawArrowH(0, W, cotaY)
  // líneas de referencia verticales
  ctx.lineWidth = 0.6
  ctx.setLineDash([2, 2])
  ctx.beginPath(); ctx.moveTo(0, H); ctx.lineTo(0, H + COTA); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(W, H); ctx.lineTo(W, H + COTA); ctx.stroke()
  ctx.setLineDash([])
  // texto
  const labelW = `${pX}`
  const tw = ctx.measureText(labelW).width + 6
  ctx.fillStyle = isDark ? '#1f2937' : '#ffffff'
  ctx.fillRect(W / 2 - tw / 2, cotaY - fontSize * 0.7, tw, fontSize * 1.4)
  ctx.fillStyle = cotaColor
  ctx.fillText(labelW, W / 2, cotaY)

  // Cota vertical (alto = pY) — a la derecha del papel
  const cotaX = W + COTA * 0.6
  drawArrowV(cotaX, 0, H)
  ctx.lineWidth = 0.6
  ctx.setLineDash([2, 2])
  ctx.beginPath(); ctx.moveTo(W, 0); ctx.lineTo(W + COTA, 0); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(W, H); ctx.lineTo(W + COTA, H); ctx.stroke()
  ctx.setLineDash([])
  // texto rotado
  const labelH = `${pY}`
  ctx.save()
  ctx.translate(cotaX, H / 2)
  ctx.rotate(-Math.PI / 2)
  const th = ctx.measureText(labelH).width + 6
  ctx.fillStyle = isDark ? '#1f2937' : '#ffffff'
  ctx.fillRect(-th / 2, -fontSize * 0.7, th, fontSize * 1.4)
  ctx.fillStyle = cotaColor
  ctx.fillText(labelH, 0, 0)
  ctx.restore()
}
</script>

<style scoped>
/* Drawer slide-in desde la derecha */
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
}
.drawer-enter-to,
.drawer-leave-from {
  transform: translateX(0);
}

/* Fade para overlay y mensajes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Animación de resultado */
.result-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.result-leave-active {
  transition: all 0.15s ease;
}
.result-enter-from {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}
.result-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
